"use client"

import { fetchCampaignsData } from "@/(admin)/modules/stores/services/campaigns";
import { t } from "@/languages/i18n";
import { DeleteOutlined, HolderOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Select, Space, Spin } from "antd";
import { debounce } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";

export const CampaignsSelect: React.FC<{ value?: any[], onChange?: (v: any[]) => void }> = ({ value = [], onChange }) => {
    const [options, setOptions] = useState<any[]>([]);
    const [rawApiData, setRawApiData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const isFetching = useRef(false);
    const lastProcessedValue = useRef("");

    const loadDetails = async (ids: (number)[]) => {
        const currentString = JSON.stringify([...ids].sort());
        if (!ids.length || isFetching.current || currentString === lastProcessedValue.current) return;
        const existingIds = rawApiData.map(item => String(item.id));
        const missingIds = ids.filter(id => !existingIds.includes(String(id)));

        if (missingIds.length === 0) {
            lastProcessedValue.current = currentString;
            return;
        }

        isFetching.current = true;
        setLoading(true);

        try {
            const cleanIds = missingIds.map(id => Number(id)).filter(id => !isNaN(id));
            const chunkSize = 30;
            let newFetchedItems: any[] = [];

            for (let i = 0; i < cleanIds.length; i += chunkSize) {
                const chunk = cleanIds.slice(i, i + chunkSize);
                const data = await fetchCampaignsData({ "filters[id][$in]": chunk });
                if (Array.isArray(data)) newFetchedItems = [...newFetchedItems, ...data];
            }

            setRawApiData(prev => {
                const combined = [...prev, ...newFetchedItems];

                return Array.from(new Map(combined.map(item => [String(item.id), item])).values());
            });

            lastProcessedValue.current = currentString;
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    };

    const handleLoadOptions = async (search?: string) => {
        const data = await fetchCampaignsData({
            "filters[name][$containsi]": search || undefined,
            "pagination[limit]": 100
        });
        setOptions(Array.isArray(data) ? data : []);
    };

    useEffect(() => { loadDetails(value); }, [value]);
    useEffect(() => { handleLoadOptions(); }, []);

    return (
        <div className="w-full">
            <div className="mb-2 font-bold text-[11px] flex justify-between uppercase text-gray-400">
                <span>{t("Chiến dịch liên kết")} ({value.length})</span>
                {loading && <Spin size="small" className="ml-2" />}
            </div>

            <div className="flex gap-2 mb-4">
                <Select
                    showSearch
                    placeholder={t("Nhấp để chọn...")}
                    onSearch={useMemo(() => debounce(handleLoadOptions, 500), [])}
                    onSelect={(val) => {
                        const next = [...value, val];
                        onChange?.(next);
                        const selected = options.find(o => String(o.id) === String(val));
                        if (selected) setRawApiData(prev => [...prev, selected]);
                    }}
                    style={{ width: "100%" }}
                    value={null}
                    filterOption={false}
                >
                    {options
                        .filter(opt => !value.map(v => String(v)).includes(String(opt.id)))
                        .map((opt) => (
                            <Select.Option key={opt.id} value={opt.id}>
                                {opt.attributes?.name || opt.name}
                            </Select.Option>
                        ))
                    }
                </Select>
                <Button icon={<ReloadOutlined />} onClick={() => { lastProcessedValue.current = ""; loadDetails(value); handleLoadOptions(); }} />
            </div>

            <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto border border-dashed p-2 bg-gray-50 rounded shadow-inner">
                {value.map((id) => {
                    const match = rawApiData.find(item => String(item.id) === String(id));
                    return (
                        <div key={id} className="flex items-center justify-between p-2 bg-white border rounded shadow-sm hover:border-blue-300 transition-all">
                            <Space>
                                <HolderOutlined className="text-gray-300" />
                                <span className="text-blue-600 font-medium text-sm">
                                    {match?.attributes?.name || match?.name || `ID: ${id} (Đang tải...)`}
                                </span>
                            </Space>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />}
                                onClick={() => onChange?.(value.filter(v => String(v) !== String(id)))}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};